import { getStoredUserString, setStoredUserString } from "@/lib/auth-storage";
import { SESSION_EXPIRED_EVENT } from "@/lib/session-expiration";
import { clearWhatsAppReconnectDismissed } from "@/lib/whatsapp-reconnect";
import Cookies from "js-cookie";
import React, { useCallback, useEffect, useState } from "react";
import ReactGA from "react-ga4";
import { clarity } from "react-microsoft-clarity";

import { CsrfService } from "../services/csrf-service";
import { SupplierUserService } from "../services/supplier-user-service";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const FRONTEND_URL = import.meta.env.VITE_FRONTEND_URL;

type User = {
  display: string;
  email: string;
  has_usable_password: boolean;
  id: number;
  avatar_url?: string;
  picture?: string;
  supplier: string;
  username: string;
  supplierId: number;
  supplierRole: string;
};

export interface AuthContextType {
  isLoading: boolean;
  isAuthenticated: boolean;
  signUp: (
    email: string,
    username: string,
    password: string,
    redirect?: string,
  ) => Promise<void>;
  signIn: (
    username: string,
    password: string,
    redirect?: string,
  ) => Promise<void>;
  attachProvider: (provider: string, type: "login" | "connect") => void;
  signOut: () => Promise<void>;
  user: User | null;
  checkAuthentication: () => Promise<boolean>;
}

const AuthContext = React.createContext<AuthContextType | null>(null);

function readStoredUser(): User | null {
  const storedUser = getStoredUserString();

  if (!storedUser) {
    return null;
  }

  try {
    return JSON.parse(storedUser) as User;
  } catch {
    setStoredUserString(null);
    return null;
  }
}

export const getCsrfToken = () => {
  // Try different possible CSRF cookie names
  const token =
    Cookies.get("csrftoken") ||
    Cookies.get("csrf_token") ||
    Cookies.get("CSRF-TOKEN");

  return token;
};

// Function to fetch a CSRF token if missing
export const fetchCsrfToken = async (): Promise<string | undefined> => {
  try {
    // Make a simple GET request to the backend to set the CSRF cookie
    const response = await CsrfService.getCsrfToken();

    if (response) {
      // After the request, the CSRF token should be set in cookies
      return Cookies.get("csrftoken");
    }

    return undefined;
  } catch (error) {
    // console.error("Error fetching CSRF token:", error);
    return undefined;
  }
};

function setAnalyticsIdentity(u: User) {
  if (!u?.id) {
    return;
  }
  // GA4: set user_id + optional user_properties (non-PII)
  ReactGA.set({ user_id: String(u.id) });
  ReactGA.gtag("set", "user_properties", {
    supplierId: u.supplierId ?? undefined,
    supplierRole: u.supplierRole ?? undefined,
  });

  // Clarity: identify user (ID + friendly name)
  clarity.identify("USER_ID", { userProperty: String(u.id) });
}

function clearAnalyticsIdentity() {
  // GA4: clear user_id and properties
  ReactGA.set({ user_id: undefined });
  ReactGA.gtag("set", "user_properties", {} as Record<string, never>);

  // Clarity has no “clear” API; we simply stop identifying until next login.
  // Optionally stop capture on logout:
  // clarity.stop();
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isLoading, setLoading] = useState(true);
  const [isLoggedIn, setLoggedIn] = useState(false);
  const user = readStoredUser();

  useEffect(() => {
    const handleSessionExpired = () => {
      setLoggedIn(false);
      setStoredUserString(null);
      clearAnalyticsIdentity();
      setLoading(false);
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);

    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    };
  }, []);

  const checkAuthentication = useCallback(async () => {
    let auth = false; // FIXME: workaround for now since beforeLoad on _main won't wait for this to run
    try {
      // Try to get CSRF token, if not available, fetch it
      let csrfToken = getCsrfToken();
      if (!csrfToken) {
        csrfToken = await fetchCsrfToken();
      }

      const response = await fetch(
        `${BACKEND_URL}/_allauth/browser/v1/auth/session`,
        {
          credentials: "include",
          headers: {
            "X-CSRFToken": csrfToken ?? "",
          },
        },
      );
      if (response.ok) {
        const res = await response.json();
        if (res.data.user) {
          // Get supplier ID for the logged-in user
          try {
            const supplierUser =
              await SupplierUserService.getSupplierUserByEmail(
                res.data.user.email,
              );
            if (supplierUser) {
              // Add supplier ID and role to the user object
              res.data.user.supplierId = supplierUser.supplier;
              res.data.user.supplierRole = supplierUser.role;
            }
          } catch (error) {
            // console.error("Error fetching supplier ID:", error);
          }

          setStoredUserString(JSON.stringify(res.data.user));
          setLoggedIn(true);
          auth = true;
        } else {
          setLoggedIn(false);
          setStoredUserString(null);
        }
      } else {
        setLoggedIn(false);
        setStoredUserString(null);
      }
    } catch (error) {
      setLoggedIn(false);
      setStoredUserString(null);
      console.error("Error checking authentication:", error);
    }
    setLoading(false);
    // console.info("Authentication status in checkAuthentication:", auth);
    return auth;
  }, []);

  const attachProvider = async (
    provider: string,
    type: "login" | "connect",
  ) => {
    try {
      // Try to get CSRF token, if not available, fetch it
      let csrfToken = getCsrfToken();
      if (!csrfToken) {
        csrfToken = await fetchCsrfToken();
        if (!csrfToken) {
          throw new Error("Could not obtain CSRF token");
        }
      }

      // Create a form and submit it to handle the redirect properly
      const form = document.createElement("form");
      form.method = "POST";
      form.action = `${BACKEND_URL}/_allauth/browser/v1/auth/provider/redirect`;
      form.style.display = "none";

      // Add CSRF token
      const csrfInput = document.createElement("input");
      csrfInput.type = "hidden";
      csrfInput.name = "csrfmiddlewaretoken";
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);

      // Add provider
      const providerInput = document.createElement("input");
      providerInput.type = "hidden";
      providerInput.name = "provider";
      providerInput.value = provider;
      form.appendChild(providerInput);

      // Add callback URL
      const callbackInput = document.createElement("input");
      callbackInput.type = "hidden";
      callbackInput.name = "callback_url";
      callbackInput.value = `${FRONTEND_URL}/sign-in`; // or `${FRONTEND_URL}/settings`
      form.appendChild(callbackInput);

      // Add process
      const processInput = document.createElement("input");
      processInput.type = "hidden";
      processInput.name = "process";
      processInput.value = type;
      form.appendChild(processInput);

      // Append form to body and submit it
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    } catch (error) {
      console.error("Error signing in with provider:", error);
    }
  };

  const signUp = async (
    email: string,
    username: string,
    password: string,
    redirect = "/",
  ) => {
    try {
      // Try to get CSRF token, if not available, fetch it
      let csrfToken = getCsrfToken();
      if (!csrfToken) {
        csrfToken = await fetchCsrfToken();
        if (!csrfToken) {
          throw new Error("Could not obtain CSRF token");
        }
      }

      const response = await fetch(
        `${BACKEND_URL}/_allauth/browser/v1/auth/signup`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
          body: JSON.stringify({ email, username, password }),
          credentials: "include",
        },
      );

      if (response.ok) {
        const res = await response.json();

        // Get supplier ID for the logged-in user
        try {
          const supplierUser = await SupplierUserService.getSupplierUserByEmail(
            res.data.user.email,
          );
          if (supplierUser) {
            // Add supplier ID and role to the user object
            res.data.user.supplierId = supplierUser.supplier;
            res.data.user.supplierRole = supplierUser.role;
          }
        } catch (error) {
          console.error("Error fetching supplier ID:", error);
        }

        setLoggedIn(true);
        setStoredUserString(JSON.stringify(res.data.user));
        setAnalyticsIdentity(res.data.user);
        window.history.pushState({}, "", redirect);
      } else {
        throw new Error("Login failed");
      }
    } catch (error) {
      console.error("Error signing in:", error);
    }
  };

  const signIn = async (username: string, password: string, redirect = "/") => {
    try {
      // Try to get CSRF token, if not available, fetch it
      let csrfToken = getCsrfToken();
      if (!csrfToken) {
        csrfToken = await fetchCsrfToken();
        if (!csrfToken) {
          throw new Error("Could not obtain CSRF token");
        }
      }

      const response = await fetch(
        `${BACKEND_URL}/_allauth/browser/v1/auth/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
          body: JSON.stringify({ username, password }),
          credentials: "include",
        },
      );

      if (response.ok) {
        const res = await response.json();

        // Get supplier ID for the logged-in user
        try {
          const supplierUser = await SupplierUserService.getSupplierUserByEmail(
            res.data.user.email,
          );
          if (supplierUser) {
            // Add supplier ID and role to the user object
            res.data.user.supplierId = supplierUser.supplier;
            res.data.user.supplierRole = supplierUser.role;
          }
        } catch (error) {
          // console.error("Error fetching supplier ID:", error);
        }

        setLoggedIn(true);
        setStoredUserString(JSON.stringify(res.data.user));
        setAnalyticsIdentity(res.data.user);
        window.history.pushState({}, "", redirect);
      } else {
        throw new Error("Login failed");
      }
    } catch (error) {
      console.error("Error signing in:", error);
    }
  };

  const signOut = async () => {
    try {
      // Try to get CSRF token, if not available, fetch it
      let csrfToken = getCsrfToken();
      if (!csrfToken) {
        csrfToken = await fetchCsrfToken();
        if (!csrfToken) {
          throw new Error("Could not obtain CSRF token");
        }
      }

      const response = await fetch(
        `${BACKEND_URL}/_allauth/browser/v1/auth/session`,
        {
          method: "DELETE",
          headers: {
            "X-CSRFToken": csrfToken,
          },
          body: JSON.stringify({
            client: "browser",
          }),
          credentials: "include",
        },
      );
      if (response.status === 401) {
        clearWhatsAppReconnectDismissed(user?.id);
        setLoggedIn(false);
        setStoredUserString(null);
        clearAnalyticsIdentity();
        window.history.pushState({}, "", "/sign-in");
      } else {
        throw new Error("Logout failed");
      }
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  return React.createElement(
    AuthContext.Provider,
    {
      value: {
        isLoading,
        isAuthenticated: isLoggedIn,
        signUp,
        signIn,
        attachProvider,
        signOut,
        user,
        checkAuthentication,
      },
    },
    children,
  );
}

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
